import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const MisInvoices = () => {
  const { API } = useContext(AuthContext);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await axios.get(`${API}/estudiantes/mis-invoices`);
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Error al cargar invoices');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (invoiceNumero) => {
    setDownloading(prev => ({ ...prev, [invoiceNumero]: true }));
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await fetch(`${API}/invoices/${invoiceNumero}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al descargar invoice');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice descargado correctamente');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Error al descargar invoice');
    } finally {
      setDownloading(prev => ({ ...prev, [invoiceNumero]: false }));
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      'pendiente': { color: 'bg-yellow-100 text-yellow-800', text: 'Pendiente de Pago' },
      'pagado': { color: 'bg-green-100 text-green-800', text: 'Pagado' },
      'vencido': { color: 'bg-red-100 text-red-800', text: 'Vencido' },
      'cancelado': { color: 'bg-gray-100 text-gray-800', text: 'Cancelado' }
    };
    const info = estados[estado] || estados['pendiente'];
    return <Badge className={info.color}>{info.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Mis Invoices</h1>
        <Badge variant="secondary" className="text-lg px-4 py-2">
          {invoices.length} {invoices.length === 1 ? 'Invoice' : 'Invoices'}
        </Badge>
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <i className="fas fa-file-invoice text-gray-300 text-6xl mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No tienes invoices</h3>
            <p className="text-gray-500">Cuando te registres a un semestre, tu invoice aparecerá aquí.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {invoices.map((invoice) => (
            <Card key={invoice.id} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl text-blue-900 mb-2">
                      <i className="fas fa-file-invoice-dollar mr-2"></i>
                      {invoice.numero_invoice}
                    </CardTitle>
                    <p className="text-sm text-blue-700 font-semibold">
                      {invoice.semestre_nombre}
                    </p>
                  </div>
                  {getEstadoBadge(invoice.estado)}
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Monto</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {invoice.moneda} {invoice.monto_total?.toLocaleString('es-UY') || '0'}
                    </p>
                    {invoice.es_becado && (
                      <Badge className="bg-green-100 text-green-800 mt-2">
                        <i className="fas fa-graduation-cap mr-1"></i>
                        Becado
                      </Badge>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Fecha de Emisión</p>
                    <p className="text-lg font-semibold text-gray-700">
                      {new Date(invoice.fecha_emision).toLocaleDateString('es-UY', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    
                    <p className="text-sm text-gray-500 mt-2 mb-1">Fecha de Vencimiento</p>
                    <p className="text-sm font-medium text-gray-700">
                      {new Date(invoice.fecha_vencimiento).toLocaleDateString('es-UY', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Método de Pago</p>
                      <p className="text-sm font-medium text-gray-700 capitalize">
                        {invoice.metodo_pago?.replace('_', ' ')}
                      </p>
                    </div>
                    
                    <Button
                      onClick={() => downloadInvoice(invoice.numero_invoice || invoice.numero || invoice.id)}
                      disabled={downloading[invoice.numero_invoice || invoice.numero || invoice.id]}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {downloading[invoice.numero_invoice || invoice.numero || invoice.id] ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Descargando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-download mr-2"></i>
                          Descargar PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {invoice.email_enviado && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <i className="fas fa-check-circle mr-2"></i>
                      Invoice enviado a tu email el {new Date(invoice.fecha_email).toLocaleDateString('es-UY')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MisInvoices;
